import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, User, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

export default function Portal() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const { user, login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const sendForgot = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("If that email exists, a reset link is on its way.");
      setForgot(false); setForgotEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Account created!");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border-b-2 border-border bg-transparent pl-9 pr-0 py-3 focus:outline-none focus:border-emerald transition-colors rounded-none placeholder:text-olive/50 text-charcoal";

  return (
    <div data-testid="portal-page" className="bg-[#F8F9FA] min-h-[calc(100vh-73px)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <div>
            <Eyebrow>Customer Portal</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl font-700 text-charcoal mt-4 leading-[1.1] tracking-tight">
              Your projects, all in one place.
            </h1>
            <p className="text-olive text-lg mt-5 leading-relaxed max-w-md">
              We're building a portal where you'll be able to view quotes, track your service schedule,
              see project photos, and message our crew. Create an account now to get early access.
            </p>
            <div className="mt-8 space-y-3">
              {["Track upcoming service visits", "View quotes & project history", "Message Adrian & David directly"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-charcoal">
                  <span className="grid place-items-center w-7 h-7 rounded-sm bg-emerald/10 text-emerald">
                    <ShieldCheck className="w-4 h-4" />
                  </span>
                  {t}
                </div>
              ))}
            </div>
            <p className="mt-8 inline-block text-sm bg-gold/15 text-gold-dark font-600 px-4 py-2 rounded-sm">
              Early access · More features coming soon
            </p>
          </div>
        </Reveal>

        <Reveal delay={1}>
          <div className="bg-white border border-border rounded-sm p-8 md:p-10 max-w-md w-full mx-auto">
            <div className="flex gap-2 mb-8 bg-[#F8F9FA] p-1 rounded-sm" data-testid="portal-tabs">
              <button
                onClick={() => setMode("login")}
                data-testid="portal-login-tab"
                className={`flex-1 py-2.5 rounded-sm font-600 text-sm transition-colors ${mode === "login" ? "bg-emerald text-white" : "text-charcoal/70"}`}
              >
                Log In
              </button>
              <button
                onClick={() => setMode("register")}
                data-testid="portal-register-tab"
                className={`flex-1 py-2.5 rounded-sm font-600 text-sm transition-colors ${mode === "register" ? "bg-emerald text-white" : "text-charcoal/70"}`}
              >
                Sign Up
              </button>
            </div>

            <form onSubmit={submit} className="space-y-6" data-testid="portal-form">
              {mode === "register" && (
                <div className="relative">
                  <User className="absolute left-0 top-3.5 w-5 h-5 text-olive/50" />
                  <input data-testid="portal-name" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => update("name", e.target.value)} required />
                </div>
              )}
              <div className="relative">
                <Mail className="absolute left-0 top-3.5 w-5 h-5 text-olive/50" />
                <input data-testid="portal-email" type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => update("email", e.target.value)} required />
              </div>
              <div className="relative">
                <Lock className="absolute left-0 top-3.5 w-5 h-5 text-olive/50" />
                <input data-testid="portal-password" type="password" className={inputCls} placeholder="Password" value={form.password} onChange={(e) => update("password", e.target.value)} required minLength={6} />
              </div>
              <button type="submit" disabled={loading} data-testid="portal-submit-btn" className="w-full inline-flex items-center justify-center gap-2 bg-gold text-charcoal px-8 py-3.5 rounded-sm font-700 hover:bg-gold-dark transition-colors disabled:opacity-60">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{mode === "login" ? "Log In" : "Create Account"} <ArrowRight className="w-5 h-5" /></>}
              </button>
              {mode === "login" && (
                <button type="button" onClick={() => setForgot((v) => !v)} data-testid="forgot-password-link" className="block w-full text-center text-sm text-emerald hover:underline">
                  Forgot password?
                </button>
              )}
            </form>

            {forgot && mode === "login" && (
              <form onSubmit={sendForgot} className="mt-5 pt-5 border-t border-border space-y-3" data-testid="forgot-form">
                <p className="text-sm text-olive">Enter your email and we'll send a reset link (valid 1 hour).</p>
                <input type="email" required value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="Your email"
                  data-testid="forgot-email-input" className="w-full border border-border rounded-sm px-3 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-emerald/40" />
                <button type="submit" data-testid="forgot-submit-btn" className="w-full bg-emerald text-white px-6 py-2.5 rounded-sm font-600 hover:bg-emerald-dark transition-colors">
                  Send Reset Link
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
